import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';

interface MFAFactor {
  id: string;
  status: string;
}

interface MFAState {
  error: string;
  factorId: string;
  totpFactor: MFAFactor | null;
  qrCode: string;
  code: string;
  isMfaVerified: boolean;

  setCode: (code: string) => void;

  listFactors: () => Promise<void>;
  startEnrollment: () => Promise<void>;
  verifyEnrollment: () => Promise<boolean>;
  disableMFA: () => Promise<boolean>;
  challengeAndVerify: () => Promise<boolean>;
  markMfaVerified: () => void;
  resetMfaStatus: () => void;
}

export const useMFAStore = create<MFAState>()(
  persist(
    (set, get) => ({
      error: '',
      factorId: '',
      totpFactor: null,
      qrCode: '',
      code: '',
      isMfaVerified: false,

      setCode: code => set({ code }),

      markMfaVerified: () => set({ isMfaVerified: true }),
      resetMfaStatus: () => set({ isMfaVerified: false }),

      listFactors: async () => {
        set({ error: '' });
        const { data, error } = await supabase.auth.mfa.listFactors();
        if (error) return set({ error: error.message });

        const factor = data.totp?.find(f => f.factor_type === 'totp');
        set({
          totpFactor: factor || null,
          factorId: factor?.id || '',
        });
      },

      startEnrollment: async () => {
        set({ error: '' });

        try {
          const { data: existingFactors, error: listErr } = await supabase.auth.mfa.listFactors();
          if (listErr) throw listErr;

          const existing = existingFactors.totp?.find(f => f.factor_type === 'totp');
          if (existing) {
            // listFactors().totp is typed as verified-only, but unverified factors can still
            // surface here; widen the type so the cleanup branch below stays reachable.
            const existing_status: string = existing.status;
            if (existing_status === 'unverified') {
              await supabase.auth.mfa.unenroll({ factorId: existing.id });
            } else {
              return set({
                totpFactor: existing,
                factorId: existing.id,
                qrCode: '',
              });
            }
          }

          const { data, error } = await supabase.auth.mfa.enroll({
            factorType: 'totp',
            friendlyName: `Authenticator ${Date.now()}`,
          });

          if (error) throw error;

          set({
            factorId: data.id,
            qrCode: data.totp.qr_code,
          });
        } catch (err) {
          console.error(err);
        }
      },

      verifyEnrollment: async () => {
        const { factorId, code } = get();
        set({ error: '' });

        try {
          const challenge = await supabase.auth.mfa.challenge({ factorId });
          if (challenge.error) throw challenge.error;

          const verify = await supabase.auth.mfa.verify({
            factorId,
            challengeId: challenge.data.id,
            code,
          });

          if (verify.error) throw verify.error;

          await get().listFactors();
          return true;
        } catch (err) {
          console.error(err);
          return false;
        }
      },

      disableMFA: async () => {
        const { totpFactor, code } = get();
        if (!totpFactor) return false;

        try {
          const challenge = await supabase.auth.mfa.challenge({ factorId: totpFactor.id });
          if (challenge.error) throw challenge.error;

          const verify = await supabase.auth.mfa.verify({
            factorId: totpFactor.id,
            challengeId: challenge.data.id,
            code,
          });

          if (verify.error) throw verify.error;

          const unenroll = await supabase.auth.mfa.unenroll({ factorId: totpFactor.id });
          if (unenroll.error) throw unenroll.error;

          set({ totpFactor: null, factorId: '', qrCode: '', code: '' });
          return true;
        } catch (err) {
          console.error(err);
          return false;
        }
      },

      challengeAndVerify: async () => {
        const { code } = get();
        set({ error: '' });

        try {
          const factors = await supabase.auth.mfa.listFactors();
          if (factors.error) throw factors.error;

          const totpFactor = factors.data.totp[0];
          if (!totpFactor) throw new Error('No TOTP factor found');

          const challenge = await supabase.auth.mfa.challenge({ factorId: totpFactor.id });
          if (challenge.error) throw challenge.error;

          const verify = await supabase.auth.mfa.verify({
            factorId: totpFactor.id,
            challengeId: challenge.data.id,
            code,
          });

          if (verify.error) throw verify.error;

          return true;
        } catch (err) {
          console.error(err);
          return false;
        }
      },
    }),
    {
      name: 'mfa-store',
      partialize: state => ({ isMfaVerified: state.isMfaVerified }),
    }
  )
);
